import { useId } from "react";

export type SparkleVariant = "cluster" | "double" | "single";
export type SparkleTone = "gradient" | "inverse" | "on-dark";

interface AISparkleProps {
  variant?: SparkleVariant;
  tone?: SparkleTone;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

const PATHS: Record<SparkleVariant, string[]> = {
  cluster: [
    "M9 3C9 7.05 13.95 12 18 12C13.95 12 9 16.95 9 21C9 16.95 4.05 12 0 12C4.05 12 9 7.05 9 3Z",
    "M19.4 2.4C19.4 4.2 21.6 6.4 23.4 6.4C21.6 6.4 19.4 8.6 19.4 10.4C19.4 8.6 17.2 6.4 15.4 6.4C17.2 6.4 19.4 4.2 19.4 2.4Z",
    "M19.4 13.6C19.4 15.4 21.6 17.6 23.4 17.6C21.6 17.6 19.4 19.8 19.4 21.6C19.4 19.8 17.2 17.6 15.4 17.6C17.2 17.6 19.4 15.4 19.4 13.6Z",
  ],
  double: [
    "M9.5 0C9.5 4.275 14.725 9.5 19 9.5C14.725 9.5 9.5 14.725 9.5 19C9.5 14.725 4.275 9.5 0 9.5C4.275 9.5 9.5 4.275 9.5 0Z",
    "M18.5 13.5C18.5 15.75 21.25 18.5 23.5 18.5C21.25 18.5 18.5 21.25 18.5 23.5C18.5 21.25 15.75 18.5 13.5 18.5C15.75 18.5 18.5 15.75 18.5 13.5Z",
  ],
  single: [
    "M12 1C12 5.95 18.05 12 23 12C18.05 12 12 18.05 12 23C12 18.05 5.95 12 1 12C5.95 12 12 5.95 12 1Z",
  ],
};

const NAVY = "#102260";
const BLUE = "#176CE5";
const CYAN = "#08C6E5";

const STOPS: Record<SparkleTone, [string, string][]> = {
  gradient: [
    ["0%", NAVY],
    ["50%", BLUE],
    ["100%", CYAN],
  ],
  inverse: [
    ["0%", BLUE],
    ["100%", CYAN],
  ],
  "on-dark": [
    ["0%", CYAN],
    ["58%", BLUE],
    ["88%", BLUE],
    ["100%", NAVY],
  ],
};

const GLOSS: [string, number][] = [
  ["0%", 0.55],
  ["42%", 0.08],
  ["100%", 0],
];

export default function AISparkle({
  variant = "single",
  tone = "gradient",
  size = 16,
  className,
  style,
}: AISparkleProps) {
  const uid = useId().replace(/:/g, "");
  const bodyId = `qai-sparkle-body-${uid}`;
  const glossId = `qai-sparkle-gloss-${uid}`;
  const stops = STOPS[tone];
  const paths = PATHS[variant];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={["qai-sparkle", `qai-sparkle--${variant}`, className].filter(Boolean).join(" ")}
      data-tone={tone}
      aria-hidden="true"
      focusable="false"
      style={style}
    >
      <defs>
        <linearGradient id={bodyId} x1="0%" y1="100%" x2="100%" y2="0%">
          {stops.map(([offset, color]) => (
            <stop key={offset} offset={offset} stopColor={color} />
          ))}
        </linearGradient>
        <linearGradient id={glossId} x1="100%" y1="0%" x2="25%" y2="90%">
          {GLOSS.map(([offset, opacity]) => (
            <stop key={offset} offset={offset} stopColor="#FFFFFF" stopOpacity={opacity} />
          ))}
        </linearGradient>
      </defs>
      <g className="qai-sparkle__body">
        {paths.map((d) => (
          <path key={d} fill={`url(#${bodyId})`} d={d} />
        ))}
      </g>
      <g className="qai-sparkle__gloss">
        {paths.map((d) => (
          <path key={d} fill={`url(#${glossId})`} d={d} />
        ))}
      </g>
    </svg>
  );
}
